import useFlag from 'hooks/useFlag'
import { buildTranslate } from 'locales'
import React, { useCallback } from 'react'
import { useSelector } from 'react-redux'
import { selectStyle } from 'slices/Cms/Content/AppNavigation/Style/selectors'
import { history } from 'store'
import { SubTab, Tab as NavTab } from '../_shared/ContentNavigation'

// AppNavigation-specific tabs enum
export enum Tabs {
  AppNavigation = 'app_navigation',
  Style = 'app_navigation_style',
  HamburgerNav = 'app_navigation_hamburger_nav',
  BottomNav = 'app_navigation_bottom_nav',
}

const t = buildTranslate('cms')

const AppNavigationTabs: React.FC = () => {
  // Direct feature flag checks (not using the proxy hooks here)
  const styleEnabled = useFlag('bottom-nav-toggle-mx', false)
  const hamburgerNavEnabled = useFlag('manage-hamburger-nav-mx', false)
  const bottomNavEnabled = useFlag('customise-bottom-nav-mx', false)
  const bottomPlacementSelected =
    useSelector(selectStyle).settings?.placement === 'bottom'

  // Compute visibility for each tab
  const styleTabVisible = styleEnabled
  const hamburgerTabVisible = hamburgerNavEnabled
  const bottomTabVisible = bottomNavEnabled && bottomPlacementSelected

  // AppNavigation visibility - at least one tab must be enabled
  const isAppNavigationVisible =
    styleEnabled || hamburgerNavEnabled || bottomNavEnabled

  // Internal isTabActive logic for AppNavigation routes
  const isTabActive = useCallback((tabKey: string) => {
    const pathname = history.location.pathname

    // AppNavigation routes
    if (pathname.startsWith('/brand_content/app_navigation')) {
      if (pathname === '/brand_content/app_navigation/style') {
        return tabKey === Tabs.Style
      }
      if (pathname === '/brand_content/app_navigation/hamburger_nav') {
        return tabKey === Tabs.HamburgerNav
      }
      if (pathname === '/brand_content/app_navigation/bottom_nav') {
        return tabKey === Tabs.BottomNav
      }
      if (pathname.includes('/brand_content/app_navigation/bottom_nav/edit')) {
        return tabKey === Tabs.BottomNav
      }
      if (pathname === '/brand_content/app_navigation') {
        // Default route - show Style as active and App Navigation as active
        return tabKey === 'app_navigation' || tabKey === Tabs.Style
      }
    }

    return false
  }, [])

  // Don't render anything if AppNavigation is not visible
  if (!isAppNavigationVisible) {
    return null
  }

  return (
    <NavTab
      tabKey="app_navigation"
      name={t('content.app_navigation.tab_title')}
      active={
        isTabActive('app_navigation') ||
        isTabActive(Tabs.Style) ||
        isTabActive(Tabs.HamburgerNav) ||
        isTabActive(Tabs.BottomNav)
      }
    >
      {styleTabVisible && (
        <SubTab
          tabKey={Tabs.Style}
          name={t('content.app_navigation.style.tab_title')}
          active={isTabActive(Tabs.Style)}
        />
      )}
      {hamburgerTabVisible && (
        <SubTab
          tabKey={Tabs.HamburgerNav}
          name={t('content.app_navigation.hamburger_nav.tab_title')}
          active={isTabActive(Tabs.HamburgerNav)}
        />
      )}
      {bottomTabVisible && (
        <SubTab
          tabKey={Tabs.BottomNav}
          name={t('content.app_navigation.bottom_nav.tab_title')}
          active={isTabActive(Tabs.BottomNav)}
        />
      )}
    </NavTab>
  )
}

export default AppNavigationTabs
